import React, { useState, useEffect } from 'react';
import treatmentService from '../../services/treatmentService';
import patientService from '../../services/patientService';

const TreatmentForm = ({ treatment, onClose, onSuccess, preSelectedPatient }) => {
  const [formData, setFormData] = useState({
    patient_id: '',
    treatment_performed: '',
    pain_level: '',
    observations: '',
    prescribed_exercises: '',
    improvement_notes: '',
    session_date: new Date().toISOString().split('T')[0],
  });
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [loadingPatients, setLoadingPatients] = useState(true);

  useEffect(() => {
    fetchPatients();
    
    if (treatment) {
      setFormData({
        patient_id: treatment.patient_id || '',
        treatment_performed: treatment.treatment_performed || '',
        pain_level: treatment.pain_level || '',
        observations: treatment.observations || '',
        prescribed_exercises: treatment.prescribed_exercises || '',
        improvement_notes: treatment.improvement_notes || '',
        session_date: treatment.session_date || new Date().toISOString().split('T')[0],
      });
    } else if (preSelectedPatient) {
      setFormData(prev => ({ ...prev, patient_id: preSelectedPatient.id }));
    }
  }, [treatment, preSelectedPatient]);

  const fetchPatients = async () => {
    try {
      const response = await patientService.getAllPatients({ per_page: 100, status: 'Active' });
      setPatients(response.data.patients);
    } catch (error) {
      console.error('Error fetching patients:', error);
      setError('Failed to load patients');
    } finally {
      setLoadingPatients(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (treatment) {
        await treatmentService.updateTreatment(treatment.id, formData);
      } else {
        await treatmentService.createTreatment(formData);
      }
      onSuccess();
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save treatment');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white">
          <h2 className="text-2xl font-bold text-gray-900">
            {treatment ? 'Edit Treatment Note' : 'Add Treatment Note'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}

          <div className="space-y-4">
            {/* Patient Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Patient *
              </label>
              {loadingPatients ? (
                <div className="text-sm text-gray-500">Loading patients...</div>
              ) : (
                <select
                  name="patient_id"
                  required
                  value={formData.patient_id}
                  onChange={handleChange}
                  disabled={!!treatment}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-100"
                >
                  <option value="">Select a patient</option>
                  {patients.map((patient) => (
                    <option key={patient.id} value={patient.id}>
                      {patient.name} - {patient.condition || 'No condition specified'}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {/* Session Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Session Date *
              </label>
              <input
                type="date"
                name="session_date"
                required
                value={formData.session_date}
                onChange={handleChange}
                max={new Date().toISOString().split('T')[0]}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            {/* Treatment Performed */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Treatment Performed *
              </label>
              <textarea
                name="treatment_performed"
                required
                value={formData.treatment_performed}
                onChange={handleChange}
                rows="3"
                placeholder="Describe the treatment techniques used..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              ></textarea>
            </div>

            {/* Pain Level */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Pain Level (1-10)
              </label>
              <div className="flex items-center space-x-4">
                <input
                  type="range"
                  name="pain_level"
                  min="1"
                  max="10"
                  value={formData.pain_level || 5}
                  onChange={handleChange}
                  className="flex-1"
                />
                <span className="text-2xl font-bold text-indigo-600 w-12 text-center">
                  {formData.pain_level || '-'}
                </span>
              </div>
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>No Pain</span>
                <span>Moderate</span>
                <span>Severe</span>
              </div>
            </div>

            {/* Observations */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Observations
              </label>
              <textarea
                name="observations"
                value={formData.observations}
                onChange={handleChange}
                rows="3"
                placeholder="Patient feedback, mobility improvements, etc..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              ></textarea>
            </div>

            {/* Prescribed Exercises */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Prescribed Exercises
              </label>
              <textarea
                name="prescribed_exercises"
                value={formData.prescribed_exercises}
                onChange={handleChange}
                rows="3"
                placeholder="List exercises and home care instructions..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              ></textarea>
            </div>

            {/* Improvement Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Improvement Notes
              </label>
              <textarea
                name="improvement_notes"
                value={formData.improvement_notes}
                onChange={handleChange}
                rows="2"
                placeholder="Progress since last session..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              ></textarea>
            </div>
          </div>

          {/* Form Actions */}
          <div className="mt-6 flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || loadingPatients}
              className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Saving...' : treatment ? 'Update Treatment' : 'Save Treatment'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TreatmentForm;